import { SetStateAction, useCallback } from 'react'

import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { ILanguage } from '@/types/task.interface'
import { ISelectedPlatform, TPlatformValue } from '@/types/uploadForm.interface'

import {
	selectOpenFormModalByTaskId,
	selectPendingSrtFileNameByTaskId,
	selectSelectedPlatformByTaskId,
	selectSelectedPlatformListByTaskId,
	selectSelectedTranscribingLangByTaskId,
	selectSelectedWayByTaskId,
	selectToFollowByTaskId,
	selectTranscribingByTaskId,
	selectUploadedFormByTaskId,
	selectUploadingByTaskId,
	selectUploadingFormByTaskId
} from './formUploaderSelectors'
import {
	resetFormUploaded,
	resetFormUploading,
	resetOpenFormModal,
	resetPendingSrtFileName,
	resetSelectedPlatform,
	resetSelectedPlatformList,
	resetSelectedTranscribingLang,
	resetSelectedWay,
	resetToFollow,
	resetTranscribing,
	resetUploading,
	setFormUploaded,
	setFormUploading,
	setOpenFormModal,
	setPendingSrtFileName,
	setSelectedPlatform,
	setSelectedPlatformList,
	setSelectedTranscribingLang,
	setSelectedWay,
	setToFollow,
	setTranscribing,
	setUploading
} from './formUploaderSlice'

export const useFormUploaderTaskSession = (taskId?: string) => {
	const dispatch = useAppDispatch()
	const safeTaskId = taskId ?? ''

	const selectedPlatform = useAppSelector(
		selectSelectedPlatformByTaskId(safeTaskId)
	)
	const selectedPlatformList = useAppSelector(
		selectSelectedPlatformListByTaskId(safeTaskId)
	)
	const openFormModal = useAppSelector(selectOpenFormModalByTaskId(safeTaskId))
	const selectedWay = useAppSelector(selectSelectedWayByTaskId(safeTaskId))
	const selectedTranscribingLang = useAppSelector(
		selectSelectedTranscribingLangByTaskId(safeTaskId)
	)
	const toFollow = useAppSelector(selectToFollowByTaskId(safeTaskId))
	const pendingSrtFileName = useAppSelector(
		selectPendingSrtFileNameByTaskId(safeTaskId)
	)
	const isUploading = useAppSelector(selectUploadingByTaskId(safeTaskId))
	const isTranscribing = useAppSelector(selectTranscribingByTaskId(safeTaskId))
	const isFormUploading = useAppSelector(
		selectUploadingFormByTaskId(safeTaskId)
	)
	const isUploaded = useAppSelector(selectUploadedFormByTaskId(safeTaskId))

	const setSessionSelectedPlatform = useCallback(
		(value: TPlatformValue | null) => {
			if (!taskId) return

			dispatch(setSelectedPlatform({ taskId, value }))
		},
		[dispatch, taskId]
	)

	const setSessionSelectedPlatformList = useCallback(
		(updater: SetStateAction<ISelectedPlatform[]>) => {
			if (!taskId) return

			const next =
				typeof updater === 'function'
					? (updater as (prev: ISelectedPlatform[]) => ISelectedPlatform[])(
							selectedPlatformList
					  )
					: updater

			dispatch(setSelectedPlatformList({ taskId, list: next }))
		},
		[dispatch, taskId, selectedPlatformList]
	)

	const setSessionOpenFormModal = useCallback(
		(value: boolean) => {
			if (!taskId) return

			dispatch(setOpenFormModal({ taskId, value }))
		},
		[dispatch, taskId]
	)

	const setSessionSelectedWay = useCallback(
		(value: string) => {
			if (!taskId) return

			dispatch(setSelectedWay({ taskId, value }))
		},
		[dispatch, taskId]
	)

	const setSessionSelectedTranscribingLang = useCallback(
		(value: ILanguage | null) => {
			if (!taskId) return

			dispatch(setSelectedTranscribingLang({ taskId, value }))
		},
		[dispatch, taskId]
	)

	const setSessionToFollow = useCallback(
		(value: boolean) => {
			if (!taskId) return

			dispatch(setToFollow({ taskId, value }))
		},
		[dispatch, taskId]
	)

	const setSessionPendingSrtFileName = useCallback(
		(value: string) => {
			if (!taskId) return

			dispatch(setPendingSrtFileName({ taskId, value }))
		},
		[dispatch, taskId]
	)

	const setSessionUploading = useCallback(
		(value: SetStateAction<boolean>) => {
			if (!taskId) return

			const next = typeof value === 'function' ? value(isUploading) : value

			dispatch(setUploading({ taskId, value: next }))
		},
		[dispatch, taskId, isUploading]
	)

	const setSessionTranscribing = useCallback(
		(value: SetStateAction<boolean>) => {
			if (!taskId) return

			const next = typeof value === 'function' ? value(isTranscribing) : value

			dispatch(setTranscribing({ taskId, value: next }))
		},
		[dispatch, taskId, isTranscribing]
	)

	const setSessionUploadingForm = useCallback(
		(value: SetStateAction<boolean>) => {
			if (!taskId) return

			const next = typeof value === 'function' ? value(isFormUploading) : value

			dispatch(setFormUploading({ taskId, value: next }))
		},
		[dispatch, taskId, isFormUploading]
	)

	const setSessionUploadedForm = useCallback(
		(value: SetStateAction<boolean>) => {
			if (!taskId) return

			const next = typeof value === 'function' ? value(isUploaded) : value

			dispatch(setFormUploaded({ taskId, value: next }))
		},
		[dispatch, taskId, isUploaded]
	)

	const resetSessionState = useCallback(() => {
		if (!taskId) return

		dispatch(resetSelectedPlatform({ taskId }))
		dispatch(resetSelectedPlatformList({ taskId }))
		dispatch(resetOpenFormModal({ taskId }))
		dispatch(resetSelectedWay({ taskId }))
		dispatch(resetSelectedTranscribingLang({ taskId }))
		dispatch(resetToFollow({ taskId }))
		dispatch(resetPendingSrtFileName({ taskId }))
		dispatch(resetUploading({ taskId }))
		dispatch(resetTranscribing({ taskId }))
		dispatch(resetFormUploading({ taskId }))
		dispatch(resetFormUploaded({ taskId }))
	}, [dispatch, taskId])

	return {
		selectedPlatform,
		selectedPlatformList,
		openFormModal,
		setOpenFormModal: setSessionOpenFormModal,
		selectedWay,
		setSelectedWay: setSessionSelectedWay,
		selectedTranscribingLang,
		setSelectedTranscribingLang: setSessionSelectedTranscribingLang,
		toFollow,
		pendingSrtFileName,
		isUploading,
		isTranscribing,
		isFormUploading,
		isUploaded,
		setSelectedPlatform: setSessionSelectedPlatform,
		setSelectedPlatformList: setSessionSelectedPlatformList,
		setToFollow: setSessionToFollow,
		setPendingSrtFileName: setSessionPendingSrtFileName,
		setIsUploading: setSessionUploading,
		setIsTranscribing: setSessionTranscribing,
		setIsFormUploading: setSessionUploadingForm,
		setIsUploaded: setSessionUploadedForm,
		resetSessionState
	}
}
